import { useEffect, useRef, useState, type FormEvent } from "react";
import { useGSAP } from "@gsap/react";
import { DotOrbit } from "@paper-design/shaders-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { playFriendlyFavicon } from "./animatedFavicon";
import { callFixes, presenceSteps, services } from "./content/site";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const callAnchor = "#call";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);

    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return reduced;
}

function FriendlyMark() {
  return (
    <span className="friendly-mark-shell" aria-hidden="true">
      <svg className="friendly-mark" viewBox="0 0 250 250" focusable="false">
        <g className="friendly-mark-loop">
          <path d="M54 100h22c0 29 20 47 49 47s49-18 49-47h22c0 41-31 68-71 68s-71-27-71-68Z" />
        </g>
        <g className="friendly-mark-dot">
          <circle cx="125" cy="100" r="22" />
        </g>
      </svg>
    </span>
  );
}

function Wordmark() {
  return (
    <span className="wordmark" aria-label="warmermedia." translate="no">
      <span className="wordmark-text" aria-hidden="true">warmermedia</span>
      <FriendlyMark />
    </span>
  );
}

function MaterialStage() {
  const stageRef = useRef<HTMLElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [motionEnabled, setMotionEnabled] = useState(!reducedMotion);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (reducedMotion) setMotionEnabled(false);
  }, [reducedMotion]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: "120px" },
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return (
    <figure
      ref={stageRef}
      className="material-stage"
      aria-label="A moving digital presence made from website, search and content surfaces"
    >
      <div className="stage-shader" aria-hidden="true">
        <DotOrbit
          colorBack="#d8cbb9"
          colors={["#f7f1e8", "#201c17", "#e66a27"]}
          size={0.34}
          sizeRange={0.42}
          spreading={0.7}
          stepsPerColor={2}
          speed={motionEnabled && isVisible ? 0.18 : 0}
          frame={0.38}
          scale={0.58}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      <div className="presence-board" aria-hidden="true">
        <article className="presence-card presence-card-web">
          <div className="presence-card-browser">
            <span>warmer presence</span>
            <em>system 01</em>
          </div>
          <p className="presence-card-overline">Website</p>
          <strong>clear enough<br />to choose<span>.</span></strong>
          <div className="presence-card-action">one next step <span>↗</span></div>
        </article>

        <article className="presence-card presence-card-search">
          <div className="search-card-topline">
            <span>Search</span>
            <i />
          </div>
          <p>interior designer near me</p>
          <div className="search-answer">
            <span className="search-answer-dot" />
            <div>
              <strong>your brand</strong>
              <small>the useful answer</small>
            </div>
          </div>
        </article>

        <article className="presence-card presence-card-content">
          <div className="content-card-label">Content / this week</div>
          <div className="content-card-lines">
            <span />
            <span />
            <span />
          </div>
          <strong>useful.<br />searchable.<br />yours.</strong>
        </article>
      </div>
      <span className="period-orb" aria-hidden="true" />
      <button
        className="stage-motion-toggle"
        type="button"
        aria-pressed={motionEnabled}
        onClick={() => setMotionEnabled((enabled) => !enabled)}
      >
        {motionEnabled ? "Pause motion" : "Play motion"}
      </button>
      <figcaption className="stage-caption">
        <span>show up</span>
        <span>be found</span>
        <span>get chosen</span>
      </figcaption>
    </figure>
  );
}

function ReceiptTrace() {
  return (
    <div className="receipt-trace" aria-hidden="true">
      <svg viewBox="0 0 520 128" preserveAspectRatio="none">
        <g className="receipt-axis">
          <line x1="12" y1="34" x2="508" y2="34" />
          <line x1="12" y1="74" x2="508" y2="74" />
          <line x1="12" y1="114" x2="508" y2="114" />
        </g>
        <path
          className="receipt-path"
          pathLength="1"
          d="M18 103 C76 98 119 87 169 74 C218 61 250 69 296 64 C365 58 414 34 502 20"
        />
        <g className="receipt-nodes">
          <circle className="receipt-node" cx="169" cy="74" r="5" />
          <circle className="receipt-node" cx="502" cy="20" r="5" />
        </g>
        <g className="receipt-axis-labels">
          <text x="12" y="126">before</text>
          <text x="475" y="126">after</text>
        </g>
      </svg>
    </div>
  );
}

function ReceiptsCalibration() {
  return (
    <section className="receipts" id="receipts" aria-labelledby="receipts-title">
      <div className="receipts-copy">
        <p className="section-kicker">Receipts</p>
        <h2 id="receipts-title">Proof belongs close to the promise.</h2>
        <p>
          We will show the source, the time period and what changed. Until real
          client evidence is cleared, numbers stay off the page.
        </p>
        <div className="integrity-note">
          <span className="integrity-dot" aria-hidden="true" />
          Development calibration. No client claim.
        </div>
      </div>

      <div className="receipt-scene" aria-label="Receipts visual calibration">
        <article className="receipt-panel receipt-panel-primary">
          <div className="receipt-meta">
            <span>measurement standard</span>
            <span>01</span>
          </div>
          <p className="receipt-value">baseline → change</p>
          <p className="receipt-label">A visible result needs a visible source.</p>
          <ReceiptTrace />
        </article>

        <article className="receipt-panel receipt-panel-small receipt-panel-source">
          <span className="receipt-mini-label">source</span>
          <strong>named</strong>
          <span>before publishing</span>
        </article>

        <article className="receipt-panel receipt-panel-small receipt-panel-proof">
          <span className="receipt-mini-label">invented claims</span>
          <strong>0</strong>
          <span>by design</span>
        </article>
      </div>
    </section>
  );
}

function PresenceSystem() {
  return (
    <section className="presence-system" id="system" aria-labelledby="system-title">
      <div className="section-intro presence-intro">
        <p className="section-kicker">One connected presence</p>
        <h2 id="system-title">The pieces should make each other stronger.</h2>
        <p>
          Website, search and content work better as one path from attention to
          choice.
        </p>
      </div>

      <div className="presence-path-wrap">
        <ol className="presence-path">
          {presenceSteps.map((step) => (
            <li className="presence-step" key={step.label}>
              <span className="presence-step-dot" aria-hidden="true" />
              <strong>{step.label}</strong>
              <p>{step.detail}</p>
            </li>
          ))}
        </ol>
        <span className="presence-rail" aria-hidden="true">
          <span className="presence-rail-fill" />
          <span className="presence-path-stop" />
        </span>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="services" id="services" aria-labelledby="services-title">
      <div className="services-heading">
        <p className="section-kicker">What we shape</p>
        <h2 id="services-title">Three disciplines. One job.</h2>
        <p>Make the brand easier to find, understand and choose.</p>
      </div>

      <div className="service-list">
        {services.map((service) => (
          <article className="service-row" key={service.name}>
            <p className="service-problem">{service.problem}</p>
            <div className="service-name-wrap">
              <h3>{service.name}</h3>
              <span className="service-outcome">{service.outcome}</span>
            </div>
            <p className="service-description">{service.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function CallSection() {
  return (
    <section className="call-section" id="call" aria-labelledby="call-title">
      <div className="call-copy">
        <p className="section-kicker">One call. Three fixes.</p>
        <h2 id="call-title">Leave with a clearer next move.</h2>
        <p>
          Bring the current website, search results or content. Leave knowing
          what deserves attention first.
        </p>
        <ul className="call-fixes">
          {callFixes.map((fix) => (
            <li key={fix}>
              <span aria-hidden="true">✓</span>
              {fix}
            </li>
          ))}
        </ul>
      </div>

      <aside className="call-surface" aria-label="Request a twenty-minute call">
        <div className="call-surface-topline">
          <span>20 minutes</span>
          <span>three priorities</span>
        </div>
        <CallRequestForm />
      </aside>
    </section>
  );
}

type CallRequestResponse = {
  id?: string;
  error?: string;
};

function CallRequestForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");

  async function submitRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setError("");

    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());

    try {
      const response = await fetch("/api/book-call", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...payload,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sourceUrl: window.location.href,
        }),
      });
      const result = (await response.json().catch(() => ({}))) as CallRequestResponse;

      if (!response.ok || !result.id) {
        throw new Error(result.error || "The request could not be saved.");
      }

      setReference(result.id);
      setStatus("success");
      form.reset();
    } catch (requestError) {
      setStatus("error");
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The request could not be saved.",
      );
    }
  }

  if (status === "success") {
    return (
      <div className="call-success" role="status">
        <span className="call-success-stop" aria-hidden="true" />
        <p className="call-surface-kicker">Request received</p>
        <h3>We will take it from here.</h3>
        <p>
          Warmer Media will reply from sales@warmermedia.in to confirm a time.
          Your reference is <strong>{reference}</strong>.
        </p>
        <button className="call-reset" type="button" onClick={() => setStatus("idle")}>
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form className="call-form" onSubmit={submitRequest}>
      <div className="call-form-intro">
        <p className="call-surface-kicker">Bring the messy version.</p>
        <h3>Find the first three fixes.</h3>
      </div>

      <div className="call-form-grid">
        <label>
          <span>Name</span>
          <input name="name" autoComplete="name" required maxLength={80} />
        </label>
        <label>
          <span>Work email</span>
          <input
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            required
            maxLength={160}
          />
        </label>
        <label>
          <span>Business / brand</span>
          <input name="business" autoComplete="organization" required maxLength={120} />
        </label>
        <label>
          <span>Website <em>optional</em></span>
          <input
            name="website"
            type="url"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            maxLength={240}
            placeholder="https://yourbrand.in…"
          />
        </label>
        <label className="call-form-wide">
          <span>When usually works?</span>
          <input
            name="preferredTime"
            autoComplete="off"
            required
            maxLength={120}
            placeholder="e.g. Tuesday afternoon…"
          />
        </label>
        <label className="call-form-honeypot" aria-hidden="true">
          <span>Company website</span>
          <input name="companyWebsite" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <button className="button button-primary call-button" type="submit" disabled={status === "submitting"}>
        {status === "submitting" ? "Saving your request…" : "Request a 20-min call"}
        <span aria-hidden="true">↗</span>
      </button>
      <p className={`call-form-status${status === "error" ? " is-error" : ""}`} aria-live="polite">
        {status === "error"
          ? `${error} Email sales@warmermedia.in and we will arrange it manually.`
          : "No pitch deck. We reply from sales@warmermedia.in."}
      </p>
    </form>
  );
}

function AboutSection() {
  return (
    <section className="about-section" id="about" aria-labelledby="about-title">
      <p className="section-kicker">Why Warmer</p>
      <div className="about-layout">
        <h2 id="about-title">Digital problems rarely stay in their lane.</h2>
        <div className="about-copy">
          <p>
            A website, search and content rarely fail alone. Warmer Media
            connects them so the brand shows up with one voice and one clear
            path forward.
          </p>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="final-cta" aria-labelledby="final-title">
      <p className="section-kicker">Start with the first three fixes</p>
      <h2 id="final-title">
        Make your presence felt<span className="final-stop" aria-hidden="true">.</span>
      </h2>
      <a className="button button-primary final-button" href={callAnchor}>
        Book a 20-min call
      </a>
    </section>
  );
}

export function App() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => playFriendlyFavicon(), []);

  useGSAP(
    () => {
      const media = gsap.matchMedia();

      media.add(
        {
          motionOK: "(prefers-reduced-motion: no-preference)",
          desktop: "(min-width: 840px)",
        },
        (context) => {
          const { motionOK, desktop } = context.conditions as {
            motionOK: boolean;
            desktop: boolean;
          };

          if (!motionOK) return;

          const intro = gsap.timeline({
            defaults: { duration: 0.82, ease: "power3.out" },
          });

          intro
            .from(".site-nav", { autoAlpha: 0, y: -16, duration: 0.55 })
            .fromTo(
              ".site-nav .friendly-mark-loop",
              {
                autoAlpha: 0,
                x: -10,
                y: -34,
                rotation: -17,
                scaleX: 0.62,
                scaleY: 0.76,
                transformOrigin: "50% 68%",
              },
              {
                autoAlpha: 1,
                x: 0,
                y: 3,
                rotation: 1.5,
                scaleX: 0.9,
                scaleY: 1.08,
                duration: 0.46,
                ease: "power3.out",
              },
              0.14,
            )
            .to(
              ".site-nav .friendly-mark-loop",
              {
                y: 5,
                rotation: 0,
                scaleX: 1.2,
                scaleY: 0.74,
                duration: 0.18,
                ease: "power2.out",
              },
              0.53,
            )
            .to(
              ".site-nav .friendly-mark-loop",
              {
                y: 0,
                scaleX: 1,
                scaleY: 1,
                duration: 0.38,
                ease: "back.out(1.35)",
                clearProps: "transform",
              },
              0.7,
            )
            .from(
              ".site-nav .friendly-mark-dot",
              {
                autoAlpha: 0,
                y: -6,
                scale: 0.45,
                duration: 0.34,
                ease: "back.out(1.7)",
                transformOrigin: "50% 50%",
              },
              0.4,
            )
            .from(
              ".hero-kicker, .hero-title-line, .hero-copy, .hero-actions, .hero-note",
              { autoAlpha: 0, y: 30, stagger: 0.075 },
              "-=0.28",
            )
            .from(
              ".hero-period",
              { scale: 0, rotation: -24, transformOrigin: "50% 70%" },
              "-=0.55",
            )
            .from(
              ".material-stage",
              { autoAlpha: 0, y: desktop ? 28 : 16, scale: 0.985 },
              "-=0.68",
            )
            .from(
              ".stage-shader, .presence-card, .stage-caption span",
              { autoAlpha: 0, y: 18, stagger: 0.06 },
              "-=0.5",
            )
            .from(
              ".period-orb",
              {
                autoAlpha: 0,
                x: desktop ? -32 : -18,
                y: desktop ? -42 : -24,
                scale: 0.45,
                rotation: -18,
              },
              "-=0.46",
            );

          gsap.to(".presence-card-web", {
            y: desktop ? -26 : -12,
            rotation: -1.2,
            ease: "none",
            scrollTrigger: {
              trigger: ".hero",
              start: "top top",
              end: "bottom top",
              scrub: 0.65,
            },
          });

          gsap.to(".presence-card-search", {
            x: desktop ? -18 : -8,
            y: desktop ? 24 : 10,
            rotation: 2,
            ease: "none",
            scrollTrigger: {
              trigger: ".hero",
              start: "top top",
              end: "bottom top",
              scrub: 0.65,
            },
          });

          gsap.to(".presence-card-content", {
            x: desktop ? 16 : 7,
            y: desktop ? -14 : -6,
            rotation: -2,
            ease: "none",
            scrollTrigger: {
              trigger: ".hero",
              start: "top top",
              end: "bottom top",
              scrub: 0.65,
            },
          });

          gsap.to(".period-orb", {
            x: desktop ? -30 : -12,
            y: desktop ? 34 : 18,
            ease: "none",
            scrollTrigger: {
              trigger: ".hero",
              start: "top top",
              end: "bottom top",
              scrub: 0.65,
            },
          });

          gsap.from(".receipts-copy > *", {
            autoAlpha: 0,
            y: 38,
            stagger: 0.08,
            duration: 0.82,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".receipts",
              start: "top 76%",
              once: true,
            },
          });

          const receiptTimeline = gsap.timeline({
            defaults: { duration: 1, ease: desktop ? "none" : "power3.out" },
            scrollTrigger: desktop
              ? {
                  trigger: ".receipt-scene",
                  start: "top 88%",
                  end: "top 42%",
                  scrub: 0.7,
                }
              : {
                  trigger: ".receipt-scene",
                  start: "top 84%",
                  once: true,
                },
          });

          receiptTimeline
            .from(
              ".receipt-panel-primary",
              {
                autoAlpha: 0,
                y: desktop ? -190 : -90,
                scale: 0.9,
                rotation: -8,
                transformOrigin: "50% 0%",
              },
              0,
            )
            .from(
              ".receipt-panel-source",
              { autoAlpha: 0, x: -110, y: 100, rotation: -10, scale: 0.9 },
              0.34,
            )
            .from(
              ".receipt-panel-proof",
              { autoAlpha: 0, x: 115, y: 110, rotation: 10, scale: 0.9 },
              0.4,
            )
            .from(
              ".receipt-path",
              { strokeDashoffset: 1, duration: 0.42 },
              0.68,
            )
            .from(
              ".receipt-node",
              { scale: 0, stagger: 0.09, duration: 0.25 },
              0.78,
            );

          gsap.from(".presence-intro > *", {
            autoAlpha: 0,
            y: 28,
            stagger: 0.08,
            duration: 0.78,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".presence-system",
              start: "top 72%",
              once: true,
            },
          });

          gsap.from(".presence-step", {
            autoAlpha: 0,
            y: 22,
            stagger: 0.11,
            duration: 0.68,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".presence-path",
              start: "top 82%",
              once: true,
            },
          });

          if (desktop) {
            gsap.fromTo(
              ".presence-rail-fill",
              { scaleX: 0 },
              {
                scaleX: 1,
                ease: "none",
                scrollTrigger: {
                  trigger: ".presence-path",
                  start: "top 78%",
                  end: "bottom 56%",
                  scrub: 0.45,
                },
              },
            );
          }

          gsap.from(".services-heading > *, .service-row", {
            autoAlpha: 0,
            y: 34,
            stagger: 0.075,
            duration: 0.76,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".services",
              start: "top 74%",
              once: true,
            },
          });

          gsap.from(".call-copy > *, .call-surface", {
            autoAlpha: 0,
            y: 36,
            stagger: 0.08,
            duration: 0.82,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".call-section",
              start: "top 72%",
              once: true,
            },
          });

          gsap.from(".about-section > *, .about-copy > *", {
            autoAlpha: 0,
            y: 30,
            stagger: 0.08,
            duration: 0.78,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".about-section",
              start: "top 72%",
              once: true,
            },
          });

          gsap.from(".final-cta > *", {
            autoAlpha: 0,
            y: 30,
            stagger: 0.08,
            duration: 0.78,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".final-cta",
              start: "top 76%",
              once: true,
            },
          });
        },
        pageRef.current ?? undefined,
      );

      return () => media.revert();
    },
    { scope: pageRef },
  );

  return (
    <div className="site-shell" ref={pageRef}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-nav">
        <a className="brand-link" href="#main">
          <Wordmark />
        </a>
        <span className="nav-ribbon" aria-hidden="true">
          Websites <i /> SEO + GEO <i /> Content
        </span>
        <nav aria-label="Primary navigation">
          <a className="nav-text-link" href="#system">
            Presence
          </a>
          <a className="nav-text-link" href="#services">
            What we do
          </a>
          <a className="nav-text-link" href="#about">
            About
          </a>
          <details className="mobile-menu">
            <summary>Explore</summary>
            <div className="mobile-menu-links">
              <a href="#system">Presence</a>
              <a href="#services">What we do</a>
              <a href="#receipts">Receipts</a>
              <a href="#about">About</a>
            </div>
          </details>
          <a className="nav-cta" href={callAnchor}>
            Book a 20-min call
          </a>
        </nav>
      </header>

      <main id="main">
        <section className="hero" id="presence" aria-labelledby="hero-title">
          <div className="hero-copy-column">
            <p className="hero-kicker">Websites · findability · content</p>
            <h1 id="hero-title" className="hero-title" aria-label="a digital presence company.">
              <span className="hero-title-line">a digital</span>
              <span className="hero-title-line">presence</span>
              <span className="hero-title-line">
                company<span className="hero-period" aria-hidden="true">.</span>
              </span>
            </h1>
            <p className="hero-copy">
              Websites, search and content shaped as one presence people can
              find, trust and choose.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href={callAnchor}>
                Book a 20-min call
              </a>
              <a className="button button-secondary" href="#receipts">
                How proof works <span aria-hidden="true">↘</span>
              </a>
            </div>
            <p className="hero-note">One clear call. Three priorities.</p>
          </div>

          <MaterialStage />
        </section>

        <PresenceSystem />
        <Services />
        <ReceiptsCalibration />
        <CallSection />
        <AboutSection />
        <FinalCta />
      </main>

      <footer className="site-footer">
        <a className="brand-link" href="#main">
          <Wordmark />
        </a>
        <p>© 2026 warmermedia. All rights reserved.</p>
        <a href="mailto:sales@warmermedia.in">sales@warmermedia.in</a>
      </footer>
    </div>
  );
}
